import {
  TChatMessage,
  TChatPreChatFormSubmissionMessage,
  TChatTextMessage,
} from '~/features/chatbot/models'
import { isPreChatFormSubmissionMessage } from '~/features/chatbot/utils'
import {
  CHAT_V2_FORM_SUBMISSION_MESSAGE_TYPE,
  EChatV2ServerPath,
  TChatV2CachedHistoryMessage,
  TChatV2FormSubmissionMessageDetails,
  TChatV2FormSubmissionWireMessage,
  TPriorSubmission,
} from '~/features/chatbotV2/models'
import {
  EFormDisplayRule,
  EFormFieldMapping,
  EPreChatFormKeys,
  TGuestPreChatForm,
} from '~/features/preChatForms/models'
import { EOptionalEnvironmentVariables } from '~/models/config'
import { EMessageSpeaker } from '~/models/features'
import { store } from '~/store'

export const resolveChatV2ConversationId = (
  payload: Record<string, unknown>,
) => {
  if (
    typeof payload.conversationId === 'string' &&
    payload.conversationId.length > 0
  ) {
    return payload.conversationId
  }

  if (typeof payload.sessionId === 'string' && payload.sessionId.length > 0) {
    return payload.sessionId
  }

  return ''
}

export const queryKeys = {
  // Deliberately NOT keyed on guestToken — a guest JWT legitimately rotates
  // within the same session (every reconnect mints a new one, and so does
  // refreshGuestToken's self-heal on a 401), and guestToken updating the
  // query's cache KEY meant react-query treated a routine refresh as a
  // brand new query: the just-healed result got cached under a key nothing
  // would ever look up again, isFetched reset to false, and the pre-chat
  // form could flicker/re-ask (Cursor-flagged, Medium severity). agentId is
  // the only thing that actually identifies which form-submission state this
  // is — the endpoint itself is scoped server-side by the verified token's
  // own guestId claim, not by anything the frontend needs to echo into the
  // cache key.
  latestFormSubmission: ({ agentId }: { agentId: string }) =>
    ['chatV2', EChatV2ServerPath.formSubmissionsLatest, agentId] as const,
}

export const mutationKeys = {
  preChatFormSubmit: (conversationId: string) =>
    ['preChatFormSubmit', conversationId] as const,
}

const getChatV2TokenUrl = () =>
  import.meta.env[EOptionalEnvironmentVariables.VITE_CHAT_V2_TOKEN_URL] as
    | string
    | undefined

const getChatV2BaseUrl = () => {
  const baseUrl = getChatV2TokenUrl()
  if (!baseUrl) {
    throw new Error(
      'VITE_CHAT_V2_TOKEN_URL is not set — cannot call chat-service guest endpoints.',
    )
  }

  return baseUrl
}

export class GuestChatHttpError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'GuestChatHttpError'
    this.status = status
  }
}

// Guest JWTs are deliberately short-lived (5 minutes — see tokenRequest.ts's
// expiresIn) and are only re-minted when the WebSocket itself reconnects.
// restartConversation ("reload" an active conversation) reuses an
// already-open socket without reconnecting when it can, so a REST call made
// well into a long-running conversation can 401 on an otherwise-healthy
// session purely because its token aged out — confirmed root cause of a
// real "GET /chat/form-submissions/latest" 401 on an active conversation.
// `refreshToken`, when the caller provides one (see useChatV2's
// refreshGuestToken), self-heals that: on a 401, mint a fresh token and
// retry this exact request ONCE before giving up. Logs every step of that
// recovery (the 401, the refresh outcome, the retry outcome) — previously
// none of it was logged anywhere, so a failure in this path had zero
// visibility in production (Cursor-flagged, Medium severity): an operator
// saw only the guest-facing generic form error, with no way to tell a token
// remint failed, let alone why.
async function requestWithGuestTokenRefresh<T>(
  buildRequest: (guestToken: string) => Promise<Response>,
  guestToken: string,
  path: string,
  refreshToken?: () => Promise<string | null>,
): Promise<T> {
  const response = await buildRequest(guestToken)
  if (response.ok) return response.json() as Promise<T>

  if (response.status === 401 && refreshToken) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ChatV2] ${path} got 401 — attempting a token refresh and one retry`,
    )
    const freshToken = await refreshToken()
    if (freshToken) {
      const retryResponse = await buildRequest(freshToken)
      if (retryResponse.ok) return retryResponse.json() as Promise<T>
      // eslint-disable-next-line no-console
      console.error(`[ChatV2] ${path} still failed after a token refresh`, {
        status: retryResponse.status,
      })
      throw new GuestChatHttpError(
        `Guest chat request failed with status ${retryResponse.status} after refreshing an expired token`,
        retryResponse.status,
      )
    }
    // eslint-disable-next-line no-console
    console.error(
      `[ChatV2] ${path}: token refresh did not produce a usable token`,
    )
  }

  throw new GuestChatHttpError(
    `Guest chat request failed with status ${response.status}`,
    response.status,
  )
}

export const guestChatGet = async <T>({
  guestToken,
  path,
  refreshToken,
}: {
  guestToken: string
  path: string
  /** Called once on a 401 to mint a fresh guest token; the request is then
   * retried exactly once with it. Omit to leave a 401 as a hard failure. */
  refreshToken?: () => Promise<string | null>
}): Promise<T> =>
  requestWithGuestTokenRefresh<T>(
    (token) =>
      fetch(`${getChatV2BaseUrl()}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    guestToken,
    path,
    refreshToken,
  )

export const guestChatPost = async <T>({
  guestToken,
  path,
  payload,
  refreshToken,
}: {
  guestToken: string
  path: string
  payload: unknown
  /** Called once on a 401 to mint a fresh guest token; the request is then
   * retried exactly once with it. Omit to leave a 401 as a hard failure. */
  refreshToken?: () => Promise<string | null>
}): Promise<T> =>
  requestWithGuestTokenRefresh<T>(
    (token) =>
      fetch(`${getChatV2BaseUrl()}${path}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }),
    guestToken,
    path,
    refreshToken,
  )

export const shouldShowPreChatForm = ({
  form,
  isPriorSubmissionResolved,
  priorSubmission,
}: {
  form?: TGuestPreChatForm
  isPriorSubmissionResolved: boolean
  priorSubmission?: TPriorSubmission | null
}) => {
  const fields = form?.[EPreChatFormKeys.fields] ?? []
  if (!form || fields.length === 0) {
    return false
  }

  if (form[EPreChatFormKeys.displayRule] === EFormDisplayRule.oncePerGuestId) {
    if (!isPriorSubmissionResolved) {
      return false
    }

    return priorSubmission === null
  }

  return true
}

const PRE_CHAT_FORM_SUBMITTED_STORAGE_KEY_PREFIX =
  'heylibby-prechat-form-submitted-'

const getPreChatFormSubmittedStorageKey = (agentId: string) =>
  `${PRE_CHAT_FORM_SUBMITTED_STORAGE_KEY_PREFIX}${agentId}`

const readPreChatFormSubmittedConversationIds = (
  agentId: string,
): Set<string> => {
  if (!agentId) {
    return new Set()
  }

  try {
    const raw = localStorage.getItem(getPreChatFormSubmittedStorageKey(agentId))
    if (!raw) {
      return new Set()
    }

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return new Set()
    }

    return new Set(
      parsed.filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      ),
    )
  } catch {
    return new Set()
  }
}

export const hasPreChatFormSubmittedForConversation = (
  agentId: string,
  conversationId: string,
) => {
  if (!agentId || !conversationId) {
    return false
  }

  return readPreChatFormSubmittedConversationIds(agentId).has(conversationId)
}

export const markPreChatFormSubmittedForConversation = (
  agentId: string,
  conversationId: string,
) => {
  if (!agentId || !conversationId) {
    return
  }

  try {
    const ids = readPreChatFormSubmittedConversationIds(agentId)
    ids.add(conversationId)
    localStorage.setItem(
      getPreChatFormSubmittedStorageKey(agentId),
      JSON.stringify([...ids]),
    )
  } catch {
    // Best-effort — server already accepted the submission.
  }
}

// heyLibbyChat details.fields are label/value only — no fieldMapping or
// sortOrder. Use a mapping that never pairs into the first/last name row;
// display order follows the wire array (stable sort when sortOrder ties).
const SERVER_FORM_SUBMISSION_LAYOUT_MAPPING = EFormFieldMapping.contactEmail

const parseFormSubmissionMessageDetails = (
  details: string | TChatV2FormSubmissionMessageDetails | undefined,
): TChatV2FormSubmissionMessageDetails | null => {
  if (!details) {
    return null
  }

  if (typeof details === 'object') {
    return Array.isArray(details.fields) ? details : null
  }

  try {
    const parsed = JSON.parse(details) as TChatV2FormSubmissionMessageDetails
    return Array.isArray(parsed.fields) ? parsed : null
  } catch {
    return null
  }
}

export const mapFormSubmissionDetailsToChatMessage = (
  details: TChatV2FormSubmissionMessageDetails,
): TChatPreChatFormSubmissionMessage | null => {
  if (details.fields.length === 0) {
    return null
  }

  return {
    speaker: EMessageSpeaker.user,
    preChatFormSubmission: {
      formSubmissionId: details.formSubmissionId,
      fields: details.fields.map((field) => ({
        displayName: field.label,
        value: field.value,
        sortOrder: 0,
        fieldMapping: SERVER_FORM_SUBMISSION_LAYOUT_MAPPING,
      })),
    },
  }
}

export const mapFormSubmissionWireToChatMessage = (
  wire: Pick<TChatV2FormSubmissionWireMessage, 'type' | 'details'> & {
    type?: string
  },
): TChatPreChatFormSubmissionMessage | null => {
  if (wire.type !== CHAT_V2_FORM_SUBMISSION_MESSAGE_TYPE) {
    return null
  }

  return mapFormSubmissionDetailsToChatMessage(wire.details)
}

export const mapCachedHistoryEntryToChatMessage = (
  entry: TChatV2CachedHistoryMessage,
): TChatMessage | null => {
  if (entry.type === CHAT_V2_FORM_SUBMISSION_MESSAGE_TYPE) {
    const submissionDetails = parseFormSubmissionMessageDetails(entry.details)
    return submissionDetails
      ? mapFormSubmissionDetailsToChatMessage(submissionDetails)
      : null
  }

  if (typeof entry.content !== 'string') {
    return null
  }

  const speaker =
    entry.role === 'user' ? EMessageSpeaker.user : EMessageSpeaker.assistant

  const textMessage: TChatTextMessage = {
    speaker,
    message: entry.content,
  }

  return textMessage
}

export const appendChatbotMessageFromServer = (message: TChatMessage) => {
  const { chatbot, setChatbot } = store.getState()

  if (isPreChatFormSubmissionMessage(message)) {
    const { formSubmissionId } = message.preChatFormSubmission
    if (
      formSubmissionId &&
      chatbot.messages.some(
        (existing) =>
          isPreChatFormSubmissionMessage(existing) &&
          existing.preChatFormSubmission.formSubmissionId === formSubmissionId,
      )
    ) {
      return
    }
  }

  setChatbot({
    messages: [...chatbot.messages, message],
  })
}
